import { forwardRef, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { Targeting } from "~/components/advertisement/ad-slots/types";
import { isArticleVideoAsHero } from "~/components/content/content-cover-video/helpers";
import type { DefaultComponentsProvider } from "~/components/content/content-item-render";
import { EntityLink } from "~/components/entity-link";
import { MagazinesStyleVariant } from "~/components/section/section-style/const";
import { sanitizeReadonlyArray } from "~/lib/utils";
import type { magazinesStyleLeadContentItemContent$key } from "~/queries/__generated__/magazinesStyleLeadContentItemContent.graphql";

import {
  AuthorNameContainer,
  AuthorNameWrapper,
  Container,
  Cover,
  CoverContainer,
  Headline,
  StoryLabel,
  StyledAuthorName,
  StyledEntityLink,
} from "./styles";

export type Props = {
  advertisement?: {
    targeting: Targeting;
    zone: string;
  };
  className?: string;
  provider: DefaultComponentsProvider;
  reference: magazinesStyleLeadContentItemContent$key;
  variantProps?: ContentItemMagazinesStyleLeadVariantProps;
};

export type ContentItemMagazinesStyleLeadVariantProps = {
  withLeadingCoverVideo?: boolean;
};

export const ContentItemMagazinesStyleLead = forwardRef<HTMLDivElement, Props>(
  ({ advertisement, className, provider, reference, variantProps }, elementReference) => {
    const content = useFragment(
      graphql`
        fragment magazinesStyleLeadContentItemContent on Content {
          authors {
            ...authorNameAuthor
          }
          ...entityLink
          ...helpersIsArticleVideoAsHeroArticle
        }
      `,
      reference,
    );

    const cover = (
      <CoverContainer>
        {isArticleVideoAsHero(content) && variantProps?.withLeadingCoverVideo ? (
          provider.coverVideo({
            advertising: {
              ...advertisement,
              instream: "instream3",
            },
          })
        ) : (
          <EntityLink reference={content}>
            {provider.coverImage({
              responsiveVariants: {
                desktopUp: "size1280x720",
                mobileUp: "size500x500",
                tabletUp: "size1280x720", // TODO: update variant when image-style available
              },
            })}
            {provider.video?.durationOverlay({
              responsiveVariants: { desktopUp: "large", mobileUp: "large", tabletUp: "large" },
            })}
          </EntityLink>
        )}
      </CoverContainer>
    );
    const headline = (
      <StyledEntityLink reference={content}>
        {provider.headline({ preferSocialHeadline: true, variant: MagazinesStyleVariant })}
      </StyledEntityLink>
    );

    const authors = useMemo(() => sanitizeReadonlyArray(content.authors), [content.authors]);
    const hasAuthor = authors.length > 0;
    const isMultipleAuthors = authors.length > 1;

    return (
      <Container className={className} ref={elementReference}>
        <Cover>{cover}</Cover>
        <Headline>{headline}</Headline>
        {hasAuthor && (
          <AuthorNameContainer $isMultipleAuthors={isMultipleAuthors}>
            <StoryLabel>story</StoryLabel>
            <span>
              {authors.map((author, index) => (
                <AuthorNameWrapper key={index}>
                  <StyledAuthorName reference={author} />
                </AuthorNameWrapper>
              ))}
            </span>
          </AuthorNameContainer>
        )}
      </Container>
    );
  },
);

ContentItemMagazinesStyleLead.displayName = "ContentItemMagazinesStyleLead";
